import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Spin,
  message,
  Upload,
  Button,
  Switch,
  Divider,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  useGetProductByIdQuery,
  useUpdateProductMutation,
  useGetSidesQuery,
  useGetProductsQuery,
  useGetAllProductQuery,
} from "@/services/api/product/productApi";
import { useGetCategoriesQuery } from "@/services/api/category/categoryApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import { useGetUnitOfMeasurementQuery } from "@/services/api/unitOfMeasurement/unitOfMeasurementApi";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";
import { DeleteOutline } from "@mui/icons-material";

const { TextArea } = Input;
const { Option } = Select;

/**
 * Edit Product Modal Component
 * Handles image upload via Cloudinary and sends URL to backend
 */
const EditProductModal = ({ visible, onCancel, onSuccess, productId }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [canBeAddOn, setCanBeAddOn] = useState(false);
  const [isVariant, setIsVariant] = useState(false);
  const [addDiscount, setAddDiscount] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Fetch product data
  const {
    data: productData,
    isLoading: isLoadingProduct,
    error: fetchError,
    isFetching,
  } = useGetProductByIdQuery(productId, {
    skip: !visible || !productId,
  });

  // Update product mutation
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  // Fetch categories, locations, and units
  const { data: categories = [], isLoading: categoriesLoading } =
    useGetCategoriesQuery();
  const { data: locations = [], isLoading: locationsLoading } =
    useGetLocationsQuery();
  const { data: units = [], isLoading: unitsLoading } =
    useGetUnitOfMeasurementQuery();
  const { data: sides = [], isLoading: sidesLoading } = useGetSidesQuery();
  const { data: products = [], isLoading: productsLoading } =
    //Changed to useGetAllProductQuery to fetch all products for variant selection
    useGetAllProductQuery();
  // const { data: products = [], isLoading: productsLoading } = useGetProductsQuery();

  // Extract product from response
  const product =
    productData?.data?.original || productData?.original || productData;

  // Populate form when product data is loaded
  useEffect(() => {
    if (product && visible) {
      // console.log("✏️ Populating form with data:", product);

      const locationIds = product.locations?.map((loc) => loc.id) || [];

      form.setFieldsValue({
        name: product.name || "",
        category_id: product.category?.id || product.category_id || null,
        description: product.description || "",
        ingredients: product.ingredients || "",
        amount: product.amount || 0,
        current_qty: product.current_qty || 0,
        minimum_qty: product.minimum_qty || 0,
        serving_size: product.serving_size || "",
        unit_of_measurement_id: product.unit_of_measurement_id || null,
        locations: locationIds,
        discount: product.discount || 0,
        add_ons:
          product.addons?.map((addon) => addon.id) ||
          product.product_addons?.map((addon) => addon.id) ||
          [],
        variant_product_id: product.variant_product_id || null,
      });

      setSelectedLocations(locationIds);

      // Set existing image and status
      const existingImageUrl = product.image?.url || product.product_image;
      // console.log("✏️ Existing image URL:", existingImageUrl);
      if (existingImageUrl) {
        setImageUrl(existingImageUrl);
        setOriginalImageUrl(existingImageUrl);
        setPreviewUrl(existingImageUrl);
      }
      // console.log("Image Url", imageUrl);
      setIsActive(product.status === 1 || product.status === true);
      setCanBeAddOn(
        product.can_be_addon === 1 || product.can_be_addon === true,
      );
      setIsVariant(product.is_variant === 1 || product.is_variant === true);
      setAddDiscount(!!product.discount && product.discount > 0);
    }
  }, [product, visible, form]);

  // Reset form and state when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setImageUrl(null);
      setPreviewUrl(null);
      setOriginalImageUrl(null);
      setIsActive(true);
      setCanBeAddOn(false);
      setIsVariant(false);
      setAddDiscount(false);
      setSelectedLocations([]);
    }
  }, [visible, form]);

  // Handle location selection with "All" option
  const handleLocationChange = (values) => {
    if (values.includes("all")) {
      // If "All" is selected, only keep "All"
      setSelectedLocations(["all"]);
      form.setFieldsValue({ locations: ["all"] });
    } else {
      // Regular selection
      setSelectedLocations(values);
    }
  };

  // ✅ Upload image to Cloudinary
  const handleUpload = async (file) => {
    try {
      message.loading({ content: "Uploading image...", key: "upload" });
      const url = await uploadToCloudinary(file);
      setImageUrl(url);
      message.success({
        content: "Image uploaded successfully!",
        key: "upload",
      });
    } catch (error) {
      console.error("Cloudinary upload failed:", error);
      message.error({ content: "Failed to upload image", key: "upload" });
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    setImageUrl(null);
    message.info("Image removed. The product will have no image after update.");
  };

  // ✅ Format & parse price fields
  const formatPrice = (value) =>
    value
      ? value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "";
  const parsePrice = (value) => (value ? value.replace(/,/g, "") : "");

  // Handle form submission'
  // Random note to self: Make sure to send image URL in JSON payload
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // console.log("✏️ Form values:", values);

      // Handle "All" locations selection
      let locationIds = values.locations;
      if (locationIds.includes("all")) {
        // If "All" is selected, pass all available location IDs
        locationIds = locations.map((loc) => loc.id);
      }

      // Build payload with image URL
      const payload = {
        name: values.name,
        category_id: values.category_id,
        description: values.description,
        ingredients: values.ingredients,
        amount: parseInt(parsePrice(values.amount)),
        current_qty: parseInt(values.current_qty),
        minimum_qty: parseInt(values.minimum_qty),
        serving_size: parseInt(values.serving_size), // Always required now
        unit_of_measurement_id: values.unit_of_measurement_id,
        discount:
          addDiscount && values.discount
            ? parseInt(parsePrice(values.discount))
            : 0,
        status: isActive ? 1 : 0,
        can_be_addon: canBeAddOn ? 1 : 0,
        location_id: locationIds, // Array of location IDs
      };

      // Add add-ons if selected
      if (values.add_ons?.length > 0) {
        payload.product_addon = values.add_ons;
      }

      // Include variant information - always send is_variant
      payload.is_variant = isVariant ? 1 : 0;

      // Only include variant_product_id if variant is enabled and a product is selected
      if (isVariant && values.variant_product_id) {
        payload.variant_product_id = values.variant_product_id;
      }

      // Handle image URL (send new URL or keep original, or set null if removed)
      if (imageUrl) {
        payload.product_image = imageUrl;
      } else if (imageUrl === null && originalImageUrl) {
        // Image was explicitly removed
        payload.product_image = null;
      }

      // console.log("✏️ Sending payload:", payload);

      // Call mutation with JSON payload containing image URL
      const result = await updateProduct({
        id: productId,
        requestData: payload,
      }).unwrap();

      // console.log("✅ Product updated:", result);
      message.success("Product updated successfully!");

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      onCancel();
    } catch (error) {
      console.error("❌ Error updating product:", error);
      console.error("Error details:", {
        status: error?.status,
        data: error?.data,
        message: error?.message,
      });

      if (error.errorFields || error.errors) {
        // Validation error from form.validateFields()
        message.error("Please fill all required fields");
      } else {
        message.error(error?.data?.message || "Failed to update product");
      }
    }
  };

  // Don't render modal if no productId
  if (!productId) {
    return null;
  }

  // Render error state
  const renderError = () => (
    <div className="py-8">
      <div className="text-center">
        <div className="text-lg text-red-600 mb-2">Error loading product</div>
        <div className="text-sm text-gray-600">
          {fetchError?.data?.message ||
            fetchError?.message ||
            "Unknown error occurred"}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Product ID: {productId}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 -mx-6 -mt-5 mb-3 bg-gradient-to-r from-blue-50 to-white">
          <EditOutlined className="text-blue-500 text-lg" />
          <span className="text-base font-semibold text-gray-800">
            Edit Product
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-200 -mx-6 -mb-5 bg-gray-50">
          <Button onClick={onCancel} className="px-6">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isUpdating}
            className="bg-blue-500 border-blue-500 hover:bg-blue-600 px-6"
          >
            {isUpdating ? "Updating..." : "Update Product"}
          </Button>
        </div>
      }
      width={900}
      destroyOnClose
      className="edit-product-modal"
      centered
      styles={{
        body: {
          maxHeight: "calc(100vh - 240px)",
          overflowY: "auto",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      }}
    >
      <Spin spinning={isLoadingProduct || isFetching}>
        <div className="px-2">
          {fetchError && renderError()}

          {!fetchError && !product && !isLoadingProduct && (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-500">No product data available</div>
            </div>
          )}

          {!fetchError && product && (
            <Form form={form} layout="vertical" requiredMark="optional">
              {/* PRODUCT IMAGE SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Product Image
                </h3>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-4">
                    {/* Image Preview */}
                    {previewUrl || imageUrl ? (
                      <div className="relative">
                        <div className="w-24 h-24 overflow-hidden rounded-lg border-2 border-gray-300 shadow-sm">
                          <img
                            src={previewUrl || imageUrl}
                            alt="Product preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <Button
                          type="text"
                          icon={<DeleteOutline />}
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full shadow-md hover:bg-green-600 border border-green-600"
                          size="small"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                        <span className="text-gray-400 text-3xl">📦</span>
                      </div>
                    )}

                    <div className="flex-1">
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file) => {
                          const isImage = file.type.startsWith("image/");
                          if (!isImage) {
                            message.error("You can only upload image files!");
                            return Upload.LIST_IGNORE;
                          }
                          const isLt2M = file.size / 1024 / 1024 < 2;
                          if (!isLt2M) {
                            message.error("Image must be smaller than 2MB!");
                            return Upload.LIST_IGNORE;
                          }
                          return false;
                        }}
                        onChange={async (info) => {
                          const file = info.file.originFileObj || info.file;

                          // Instant preview
                          const reader = new FileReader();
                          reader.onload = (e) => setPreviewUrl(e.target.result);
                          reader.readAsDataURL(file);

                          // Upload to Cloudinary
                          await handleUpload(file);
                        }}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className="rounded-md"
                        >
                          {imageUrl || previewUrl
                            ? "Change Image"
                            : "Upload Image"}
                        </Button>
                      </Upload>
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG, GIF (Max 2MB)
                      </p>
                      {imageUrl && imageUrl !== originalImageUrl && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          ✓ New image uploaded
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Divider className="my-4" />

              {/* BASIC INFORMATION SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                  <Form.Item
                    label="Product Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please enter product name" },
                    ]}
                    className="mb-0"
                  >
                    <Input
                      placeholder="Enter product name"
                      className="rounded-md"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Category"
                    name="category_id"
                    rules={[
                      { required: true, message: "Please select a category" },
                    ]}
                    className="mb-0"
                  >
                    <Select
                      placeholder="Select Category"
                      showSearch
                      optionFilterProp="children"
                      loading={categoriesLoading}
                      className="rounded-md"
                    >
                      {categories.map((category) => (
                        <Option key={category.id} value={category.id}>
                          {category.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                      { required: true, message: "Please enter description" },
                    ]}
                    className="mb-0 md:col-span-2"
                  >
                    <TextArea
                      rows={2}
                      placeholder="Briefly describe the product"
                      className="rounded-md"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Ingredients"
                    name="ingredients"
                    rules={[
                      { required: true, message: "Please enter ingredients" },
                    ]}
                    className="mb-0 md:col-span-2"
                  >
                    <Input
                      placeholder="Input the active ingredients"
                      className="rounded-md"
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider className="my-4" />

              {/* PRODUCT VARIANT SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Product Variant
                </h3>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <Form.Item label="" className="mb-0">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm text-gray-700 font-medium block">
                          This is a variant of another product
                        </span>
                        <span className="text-xs text-gray-500">
                          Use this when creating different versions of the same
                          dish (e.g., Egusi with Uziza and Egusi with Bitterleaf
                          are variants of Egusi Soup)
                        </span>
                      </div>
                      <Switch
                        checked={isVariant}
                        onChange={setIsVariant}
                        className="bg-gray-300"
                      />
                    </div>
                  </Form.Item>

                  {isVariant && (
                    <Form.Item
                      name="variant_product_id"
                      label="Parent Product"
                      rules={[
                        {
                          required: isVariant,
                          message: "Please select the parent product",
                        },
                      ]}
                      className="mb-0 mt-3"
                    >
                      <Select
                        placeholder="Select Parent Product"
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        className="rounded-md"
                        loading={productsLoading}
                      >
                        {products
                          ?.filter((item) => item.id !== productId)
                          .map((item) => (
                            <Option key={item.id} value={item.id}>
                              {item.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </div>
              </div>

              <Divider className="my-4" />

              {/* PRICING & INVENTORY SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Pricing & Inventory
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                  <Form.Item
                    label="Price (per unit)"
                    name="amount"
                    rules={[{ required: true, message: "Please enter price" }]}
                    className="mb-0"
                  >
                    <Input
                      prefix="₦"
                      placeholder="e.g. 500"
                      className="rounded-md"
                      onChange={(e) => {
                        const formatted = formatPrice(e.target.value);
                        e.target.value = formatted;
                        form.setFieldsValue({ amount: formatted });
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Measurement Unit"
                    name="unit_of_measurement_id"
                    rules={[{ required: true, message: "Please select unit" }]}
                    className="mb-0"
                  >
                    <Select
                      placeholder="Select unit"
                      loading={unitsLoading}
                      className="rounded-md"
                    >
                      {units.map((unit) => (
                        <Option key={unit.id} value={unit.id}>
                          {unit.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Current Quantity"
                    name="current_qty"
                    rules={[
                      {
                        required: true,
                        message: "Please enter current quantity",
                      },
                    ]}
                    className="mb-0"
                  >
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      className="rounded-md"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Minimum Stock Level"
                    name="minimum_qty"
                    rules={[
                      { required: true, message: "Please enter minimum stock" },
                    ]}
                    className="mb-0"
                  >
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      className="rounded-md"
                    />
                  </Form.Item>

                  <Form.Item
                    name="serving_size"
                    label="Serving Size"
                    rules={[
                      { required: true, message: "Please enter serving size" },
                    ]}
                    className="mb-0"
                  >
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g. 2"
                      className="rounded-md"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Available Locations"
                    name="locations"
                    rules={[
                      {
                        required: true,
                        message: "Please select at least one location",
                      },
                    ]}
                    className="mb-0"
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select Locations"
                      loading={locationsLoading}
                      className="rounded-md"
                      value={selectedLocations}
                      onChange={handleLocationChange}
                    >
                      <Option key="all" value="all">
                        All Locations
                      </Option>
                      {locations.map((loc) => (
                        <Option key={loc.id} value={loc.id}>
                          {loc.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <Divider className="my-4" />

              {/* DISCOUNT SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Discount (Optional)
                </h3>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Form.Item label="" className="mb-0">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700 font-medium">
                        Enable discount for this product
                      </span>
                      <Switch
                        checked={addDiscount}
                        onChange={setAddDiscount}
                        className="bg-gray-300"
                      />
                    </div>
                  </Form.Item>

                  {addDiscount && (
                    <Form.Item
                      label="Discount Amount"
                      name="discount"
                      className="mb-0 mt-3"
                    >
                      <Input
                        prefix="₦"
                        placeholder="e.g. 100"
                        className="rounded-md"
                        onChange={(e) => {
                          const formatted = formatPrice(e.target.value);
                          e.target.value = formatted;
                          form.setFieldsValue({ discount: formatted });
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>

              <Divider className="my-4" />

              {/* ADD-ONS SECTION */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Add-Ons (Optional)
                </h3>
                <Form.Item
                  name="add_ons"
                  label="Available Add-Ons"
                  className="mb-0"
                  tooltip="Select add-ons that customers can choose with this product"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select Add-Ons"
                    loading={sidesLoading}
                    className="rounded-md"
                    allowClear
                  >
                    {sides
                      .filter((side) => side.id !== productId)
                      .map((side) => (
                        <Option key={side.id} value={side.id}>
                          {side.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </div>

              <Divider className="my-4" />

              {/* OPTIONS SECTION */}
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Product Options
                </h3>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <Form.Item label="" className="mb-0">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-gray-700 font-medium block">
                            Can be used as an add-on
                          </span>
                          <span className="text-xs text-gray-500">
                            Allow customers to add this to other products
                          </span>
                        </div>
                        <Switch
                          checked={canBeAddOn}
                          onChange={setCanBeAddOn}
                          className="bg-gray-300"
                        />
                      </div>
                    </Form.Item>
                  </div>

                  <div className="bg-green-50 p-3 rounded-lg">
                    <Form.Item label="" className="mb-0">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-gray-700 font-medium block">
                            Active (Visible to Customers)
                          </span>
                          <span className="text-xs text-gray-500">
                            Make this product available for ordering
                          </span>
                        </div>
                        <Switch
                          checked={isActive}
                          onChange={setIsActive}
                          className="bg-gray-300"
                        />
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default EditProductModal;
